/**
 * Page heading — the H1 that sits at the top of every page, optionally
 * with a subtitle and an actions slot for buttons.
 *
 *   <PageHeading title="Manage Camps" subtitle="Create and manage..." />
 *
 *   <PageHeading
 *     title="Members"
 *     search={<Input aria-label="Search members" placeholder="Search by name…" … />}
 *     actions={<Button>Add member</Button>}
 *   />
 *
 * A page with its own topic search puts it in `search`, never on a row below
 * the title: it sits inline at the right of the H1, before the actions, at
 * one width on every page; on a phone it drops under the title at full width.
 * Filters and per-page controls stay on the row below.
 *
 *   <PageHeading
 *     title="Events"
 *     subtitle="Plan, register, attend"
 *     actions={<Button size="lg">Create Event</Button>}
 *   />
 *
 * The `data-onboarding="page-title"` attribute is preserved automatically
 * so the existing onboarding walkthroughs continue to find this element.
 */

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface PageHeadingProps {
  /** The H1 text. */
  title: React.ReactNode;
  /** Optional subtitle rendered as a paragraph below the title. */
  subtitle?: React.ReactNode;
  /** Optional right-aligned content (typically a primary CTA button). */
  actions?: React.ReactNode;
  /**
   * The page's own topic search (an <Input>). Rendered inline with the title,
   * left of `actions`, at TOKENS.page.searchWidth. Not for filters.
   */
  search?: React.ReactNode;
  /** Override the outer container className. */
  className?: string;
  /** Override the `data-onboarding` attribute target. */
  onboardingId?: string | false;
}

const PageHeading: React.FC<PageHeadingProps> = ({
  title,
  subtitle,
  actions,
  search,
  className = "",
  onboardingId = "page-title",
}) => {
  const { isDarkMode } = useTheme();

  return (
    <div
      className={`${TOKENS.page.headingMargin} flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 ${className}`}
    >
      <div className="min-w-0 flex-1">
        <h1
          {...(onboardingId ? { "data-onboarding": onboardingId } : {})}
          className={`${TOKENS.heading.page} ${getPrimaryText(isDarkMode)}`}
        >
          {title}
        </h1>
        {subtitle && (
          <p className={`mt-1 ${getSecondaryText(isDarkMode)}`}>{subtitle}</p>
        )}
      </div>
      {(search || actions) && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {search && <div className={TOKENS.page.searchWidth}>{search}</div>}
          {actions && <div className="flex-shrink-0">{actions}</div>}
        </div>
      )}
    </div>
  );
};

export default PageHeading;
